import React from 'react';
import { FaFacebook, FaTwitter, FaLinkedin, FaGithub, FaWhatsapp } from "react-icons/fa";
import { motion } from 'framer-motion';

const socials = [
  { label: "LinkedIn", icon: <FaLinkedin />, accent: "#0A66C2", href: "https://www.linkedin.com/in/mayur-adlak-31b962220/" },
  { label: "GitHub", icon: <FaGithub />, accent: "#8B949E", href: "https://github.com/Mayuradlak123" },
  { label: "WhatsApp", icon: <FaWhatsapp />, accent: "#25D366", href: "https://wa.me/9893030779?text=Hii%2C%20we%20are%20interested%20in%20your%20profile." },
  { label: "X", icon: <FaTwitter />, accent: "#1D9BF0", href: "https://x.com/MayurAdlak" },
  { label: "Facebook", icon: <FaFacebook />, accent: "#3b5998", href: "https://www.facebook.com/mayur.adlak.10/" },
];

const quickLinks = [
  { label: "About", href: "#about" },
  { label: "Coding Profile", href: "#coding" },
  { label: "Skills", href: "#skills" },
  { label: "Projects", href: "#projects" },
  { label: "Achievements", href: "#achievements" },
  { label: "Experience", href: "#experience" },
  { label: "Contact", href: "#contact" },
];

function ColumnHeading({ children }) {
  return (
    <div className="mb-5 flex items-center gap-3">
      <h3 className="whitespace-nowrap text-[11px] tracking-wide text-slate-400">
        {children}
      </h3>
      <span className="h-px flex-1 bg-gradient-to-r from-slate-700 to-transparent"></span>
    </div>
  );
}

function Footer() {
  return (
    <footer className="relative bg-slate-900 text-slate-400">
      {/* Accent rail ties the footer back to the section dividers above */}
      <div className="h-1 w-full bg-gradient-to-r from-blue-600 to-indigo-700"></div>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-3 lg:gap-14">
          {/* Identity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
          >
            <ColumnHeading>Mayur Adlak</ColumnHeading>
            <p className="text-sm leading-relaxed text-slate-400">
              Software Engineer building agentic AI systems and full-stack products, from
              multi-agent workflows to the backends that serve them.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${social.label}, opens in a new tab`}
                  style={{ '--brand': social.accent }}
                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-700/80 bg-slate-800/60 text-lg text-slate-400 transition-all duration-300 hover:-translate-y-1 hover:border-slate-600 hover:bg-slate-800 hover:text-[color:var(--brand)] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </motion.div>

          {/* Navigation */}
          <motion.nav
            aria-label="Footer"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <ColumnHeading>Explore</ColumnHeading>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="group inline-flex items-center gap-2 text-sm text-slate-400 transition-colors duration-300 hover:text-white focus:outline-none focus-visible:text-white"
                  >
                    <span className="h-px w-3 bg-slate-600 transition-all duration-300 group-hover:w-5 group-hover:bg-indigo-400"></span>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.nav>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <ColumnHeading>Get in touch</ColumnHeading>
            <ul className="flex flex-col gap-4">
              <li>
                <span className="mb-1 block text-[10px] tracking-wide text-slate-500">
                  Email
                </span>
                <a
                  href="mailto:mayuradlak030@gmail.com"
                  className="break-all text-sm text-slate-300 transition-colors duration-300 hover:text-white"
                >
                  mayuradlak030@gmail.com
                </a>
              </li>
              <li>
                <span className="mb-1 block text-[10px] tracking-wide text-slate-500">
                  Phone
                </span>
                <a
                  href="tel:+919893030779"
                  className="text-sm tabular-nums text-slate-300 transition-colors duration-300 hover:text-white"
                >
                  +91 98930 30779
                </a>
              </li>
              <li>
                <span className="mb-1 block text-[10px] tracking-wide text-slate-500">
                  Based in
                </span>
                <p className="text-sm leading-relaxed text-slate-300">
                  JB Nagar, Chakala, Andheri East
                  <br />
                  Mumbai 400059
                </p>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-slate-800 pt-6 sm:flex-row">
          <p className="text-[11px] tracking-wide text-slate-500">
            © <span className="tabular-nums">{new Date().getFullYear()}</span> Mayur Adlak
          </p>
          <p className="text-[11px] tracking-wide text-slate-500">
          </p>
        </div>
      </div>
    </footer> 
  );
}

export default Footer;
